/**
 * Marketplace error presentation (Macro #29 Stage 3, Phase E).
 *
 * Maps backend-authoritative error CODES to a localized human title + hint. The
 * UI never fabricates state: these strings only *describe* a fact the backend
 * returned (fail-closed — every message makes clear that nothing was
 * charged/changed). Copy is resolved through i18n so every locale is covered.
 */
import type { MarketplaceConfig } from "../../lib/marketplace-api";

export interface MktErrorInfo {
  title: string;
  hint: string;
  /** True when the resolution is to sign in (surfaces a sign-in CTA). */
  signIn?: boolean;
}

type TFn = (path: string, vars?: Record<string, string | number>) => string;

/** Resolve a backend error CODE to a localized title + hint via the i18n `t`. */
export function mktError(t: TFn, code: string | undefined, message?: string): MktErrorInfo {
  const e = (k: string) => t(`marketplace.errors.${k}`);
  switch (code) {
    case "UNAUTHENTICATED":
      return { title: e("unauthenticatedTitle"), hint: e("unauthenticatedHint"), signIn: true };
    case "MKT_DISABLED":
      return { title: e("disabledTitle"), hint: e("disabledHint") };
    case "MKT_CELL_LOCKED":
    case "MKT_LOCK_HOLDER_REQUIRED":
      return { title: e("cellLockedTitle"), hint: e("cellLockedHint") };
    case "MKT_NOT_ACTIVE":
    case "MKT_LISTING_NOT_FOUND":
      return { title: e("notActiveTitle"), hint: e("notActiveHint") };
    case "MKT_ORDER_EXPIRED":
      return { title: e("expiredTitle"), hint: e("expiredHint") };
    case "MKT_SELF_PURCHASE":
    case "MKT_NOT_SELLER":
      return { title: e("selfTitle"), hint: e("selfHint") };
    case "MKT_BELOW_FLOOR":
      return { title: e("belowFloorTitle"), hint: message || e("belowFloorHint") };
    case "MKT_SETTLEMENT_PENDING":
    case "MKT_SETTLEMENT_STATE":
    case "MKT_NOT_FINAL":
    case "LIVE_PENDING":
      return { title: e("settlementTitle"), hint: e("settlementHint") };
    case "IDENTITY_RECONCILIATION":
      return { title: e("reconciliationTitle"), hint: e("reconciliationHint") };
    case "SPATIAL_CONFLICT":
    case "MKT_NO_PLACEMENT":
      return { title: e("spatialTitle"), hint: e("spatialHint") };
    case "MKT_BID_TOO_LOW":
      return { title: e("bidLowTitle"), hint: message || e("bidLowHint") };
    case "MKT_BID_PERMIT_REUSED":
      return { title: e("permitReusedTitle"), hint: e("permitReusedHint") };
    case "MKT_AUCTION_ENDED":
    case "MKT_AUCTION_NOT_OPEN":
      return { title: e("auctionClosedTitle"), hint: e("auctionClosedHint") };
    case "MKT_AUCTION_NOT_STARTED":
      return { title: e("auctionNotStartedTitle"), hint: e("auctionNotStartedHint") };
    case "MKT_SELLER_CANNOT_BID":
      return { title: e("sellerBidTitle"), hint: e("sellerBidHint") };
    case "MKT_PERMIT_REFUSED":
      return { title: e("permitRefusedTitle"), hint: message || e("permitRefusedHint") };
    case "MKT_NO_COST_BASIS":
      return { title: e("noBasisTitle"), hint: e("noBasisHint") };
    case "MKT_ZERO_AMOUNT":
    case "MKT_ZERO_PRICE":
      return { title: e("zeroAmountTitle"), hint: e("zeroAmountHint") };
    default:
      return { title: e("genericTitle"), hint: message || e("genericHint") };
  }
}

/**
 * True when `address` is an enabled settlement currency per authoritative config.
 * Unknown config → do not block prematurely (returns true). A disabled currency
 * (e.g. USDT with no address) fail-closes to false.
 */
export function currencyEnabled(address: string | null | undefined, config?: MarketplaceConfig | null): boolean {
  if (!config) return true;
  if (!address) return false;
  const a = address.toLowerCase();
  const m = config.currencies.find((c) => c.address && c.address.toLowerCase() === a);
  return !!m?.enabled;
}
